import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form';
import type { ComponentProps, ComponentType, ReactNode } from 'react';

import { Button } from '../components/button.tsx';
import { Callout } from '../components/card.tsx';
import { AvatarField } from '../fields/avatar-field.tsx';
import { CheckboxField } from '../fields/checkbox.tsx';
import { OptionCardsField, SegmentsField, SwitchField } from '../fields/choice-fields.tsx';
import { DateField, DateRangeField, DateTimeField, TimeField } from '../fields/date-fields.tsx';
import { EmployeePickerField, ObjectPickerField } from '../fields/entity-pickers.tsx';
import { FileField } from '../fields/file-field.tsx';
import {
  HoursField,
  MoneyField,
  MultiplierField,
  NumberField,
  PercentField,
} from '../fields/number-fields.tsx';
import { PhoneField } from '../fields/phone-field.tsx';
import {
  AccountField,
  AddressField,
  BankField,
  CompanyField,
  RequisiteField,
} from '../fields/requisites-fields.tsx';
import { RichTextField } from '../fields/rich-text.tsx';
import { AsyncSelectField, MultiSelectField, SelectField } from '../fields/select-fields.tsx';
import { EmailField, PasswordField, TextAreaField, TextField } from '../fields/text-fields.tsx';

export const { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts();

/** Текст ошибки: строка валидатора или сообщение проблемы Standard Schema (Zod). */
export function errorText(error: unknown): string | undefined {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return undefined;
}

/**
 * Ошибка сервера для формы целиком: `formApi.setErrorMap({ onSubmit: formError(message) })` —
 * её покажет `<form.FormError />`.
 */
export const formError = (message: string) => ({ form: message, fields: {} });

/**
 * Ошибка поля из схемы — после ухода с поля или попытки отправить форму, а не на первой букве.
 * Свои ошибки ввода (непонятная дата, сумма) поля показывают сами.
 */
function useFieldError(): string | undefined {
  const field = useFieldContext<unknown>();
  const submitted = useSelector(field.form.store, (state) => state.submissionAttempts > 0);
  const { errors, isBlurred } = field.state.meta;
  return isBlurred || submitted ? errorText(errors[0]) : undefined;
}

/** Любое поле библиотеки: значение и обработчик его изменения. */
interface ValueProps {
  readonly value: unknown;
  readonly onValueChange: (value: never) => void;
  readonly error?: string | null | undefined;
}

/**
 * Поле библиотеки → поле формы: значение и изменения — из TanStack Form, ошибка — из схемы.
 * Уход фокуса ловится на обёртке `display: contents`: у полей разное устройство, а событие одно.
 */
function bound<P extends ValueProps>(Component: ComponentType<P>) {
  type Value = P['value'];
  function Bound(props: Omit<P, 'value' | 'onValueChange'>) {
    const field = useFieldContext<Value>();
    const error = useFieldError();
    const change = (value: Value) => {
      field.handleChange(value);
    };
    const own = { ...props, value: field.state.value, error: props.error ?? error, onValueChange: change };
    return (
      <div className="contents" onBlur={field.handleBlur}>
        <Component {...(own as unknown as P)} />
      </div>
    );
  }
  Bound.displayName = `Form${Component.displayName ?? Component.name}`;
  return Bound;
}

/** Флажок и переключатель: у них `checked` вместо `value`. */
function boundToggle(Component: typeof CheckboxField | typeof SwitchField) {
  function Bound(props: Omit<ComponentProps<typeof Component>, 'checked' | 'onCheckedChange'>) {
    const field = useFieldContext<boolean>();
    const error = useFieldError();
    return (
      <div className="contents" onBlur={field.handleBlur}>
        <Component
          {...props}
          error={props.error ?? error}
          checked={field.state.value}
          onCheckedChange={(checked) => {
            field.handleChange(checked);
          }}
        />
      </div>
    );
  }
  return Bound;
}

/** Кнопка отправки: крутится, пока форма отправляется; второй раз не нажать. */
function SubmitButton({
  children,
  onlyWhenChanged = false,
  disabled,
  ...props
}: Omit<ComponentProps<typeof Button>, 'type'> & {
  /** Форма-документ: кнопка активна, только когда что-то изменили (карта взаимодействия, 10). */
  onlyWhenChanged?: boolean;
}) {
  const form = useFormContext();
  const submitting = useSelector(form.store, (state) => state.isSubmitting);
  const unchanged = useSelector(form.store, (state) => state.isDefaultValue);
  return (
    <Button
      type="submit"
      variant="primary"
      loading={submitting}
      disabled={disabled === true || (onlyWhenChanged && unchanged && !submitting)}
      {...props}
    >
      {children}
    </Button>
  );
}

/** Ошибка формы целиком (сервер не принял данные) — над кнопками. */
function FormError() {
  const form = useFormContext();
  const error = useSelector(form.store, (state) => errorText(state.errorMap.onSubmit));
  return error ? <Callout>{error}</Callout> : null;
}

/** Тег `<form>`: Enter в поле отправляет, браузерная проверка отключена — проверяет схема. */
function Form({ children, className }: { children: ReactNode; className?: string }) {
  const form = useFormContext();
  return (
    <form
      noValidate
      className={className}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
    >
      {children}
    </form>
  );
}

/**
 * Формы платформы: `useAppForm` с полями библиотеки. Схема Zod — в `validators`, поля —
 * `<form.AppField name="price">{(field) => <field.MoneyField label="Цена" />}</form.AppField>`.
 */
export const { useAppForm, withForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    TextField: bound(TextField),
    TextAreaField: bound(TextAreaField),
    EmailField: bound(EmailField),
    PasswordField: bound(PasswordField),
    MoneyField: bound(MoneyField),
    PercentField: bound(PercentField),
    MultiplierField: bound(MultiplierField),
    HoursField: bound(HoursField),
    NumberField: bound(NumberField),
    DateField: bound(DateField),
    DateRangeField: bound(DateRangeField),
    TimeField: bound(TimeField),
    DateTimeField: bound(DateTimeField),
    SelectField: bound(SelectField),
    MultiSelectField: bound(MultiSelectField),
    AsyncSelectField: bound(AsyncSelectField),
    SegmentsField: bound(SegmentsField),
    OptionCardsField: bound(OptionCardsField),
    PhoneField: bound(PhoneField),
    RichTextField: bound(RichTextField),
    EmployeePickerField: bound(EmployeePickerField),
    ObjectPickerField: bound(ObjectPickerField),
    FileField: bound(FileField),
    AvatarField: bound(AvatarField),
    CompanyField: bound(CompanyField),
    BankField: bound(BankField),
    AccountField: bound(AccountField),
    RequisiteField: bound(RequisiteField),
    AddressField: bound(AddressField),
    CheckboxField: boundToggle(CheckboxField),
    SwitchField: boundToggle(SwitchField),
  },
  formComponents: { Form, SubmitButton, FormError },
});
